import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { GLOBAL_INPUT_DEBOUNCE_TIME } from '@app/core/helpers/constants';
import { NgChanges } from '@app/core/models/helpers';
import { ISelectOption } from '@app/shared/types/inputs';
import { debounce, isEqual, toNumber } from 'lodash';
import { OpenDetailsModalButtonComponent } from '@app/shared/components/open-details-modal-button/open-details-modal-button.component';

@Component({
  selector: 'app-input-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss']
})
export class SelectComponent implements OnInit, OnChanges {
  @Input() options: ISelectOption[] = [];
  @Input() defaultSelectedId?: number;
  @Input() multiple: boolean = false;
  @Input() required: boolean = false;
  @Input() submitted: boolean = false;
  @Input() dontActivateIfModal: boolean = false;
  @Input() showClear = true;
  @Input() search = false;
  @Input() large = false;
  @Input() extraButton = false;
  @Input() removeCheckbox = false;
  @Input() inputStyle = false;
  @Input() placeholder?: string;
  @Input() label?: string;
  @Input() theme: 'light' | 'grey' = 'light';
  @Input() tooltipText?: string;
  @Input() defaultSelectedItems?: ISelectOption[];
  @Input() searchOnBackend = false;
  @Input() initialError = false;

  @Output() emitIsOpened = new EventEmitter<boolean>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() emitChange = new EventEmitter<ISelectOption | ISelectOption[]>();

  public selectedItems: ISelectOption[] = [];
  public filteredOptions: ISelectOption[] = [];
  public dropdownOpened = false;
  public isErrored = false;
  public debouncedSearch = debounce(this.handleSearch, GLOBAL_INPUT_DEBOUNCE_TIME);

  private isSearched = false;

  ngOnInit(): void {
    this.filteredOptions = this.options;
    if (typeof this.defaultSelectedId === 'number') {
      this.selectedItems = [this.options[this.defaultSelectedId]];
    }
    if (!this.defaultSelectedId && this.defaultSelectedItems && this.defaultSelectedItems.length > 0) {
      this.selectedItems = [...this.defaultSelectedItems];
    }
  }

  ngOnChanges(changes: NgChanges<SelectComponent>): void {
    if (this.required && changes.submitted?.currentValue === true) {
      this.isErrored = !this.isSelectionValid();
    }

    if (changes.options?.currentValue && changes.options?.previousValue && !isEqual(changes.options.currentValue, changes.options.previousValue)) {
      this.filteredOptions = changes.options.currentValue;
    }
  }

  public clearSelection(event: Event): void {
    event.stopPropagation();
    this.selectedItems = [];
    this.emitChange.emit([]);
    if (this.required && this.submitted) {
      this.isErrored = !this.isSelectionValid();
    }
  }

  public isOptionSelected(option: ISelectOption): boolean {
    return this.selectedItems.indexOf(option) !== -1;
  }

  public toggleOptionPick(item: ISelectOption): void {
    if (!this.multiple) {
      this.selectedItems = [item];
      this.emitChange.emit(item);
      this.toggleDropdown();
      return;
    }

    if (this.isOptionSelected(item)) {
      this.selectedItems.splice(this.selectedItems.indexOf(item), 1);

      this.emitChange.emit(this.selectedItems);
      return;
    }
    this.isErrored = false;
    this.selectedItems = [...this.selectedItems, item];
    this.emitChange.emit(this.selectedItems);
  }

  public toggleDropdown() {
    if (this.dropdownOpened && this.isSearched) {
      this.handleSearch('');
    }

    this.dropdownOpened = !this.dropdownOpened;
    this.emitIsOpened.emit(this.dropdownOpened);
  }

  private handleSearch(value: string) {
    this.isSearched = !!value;

    if (this.searchOnBackend) {
      this.searchChange.emit(value);
    } else {
      this.filteredOptions = !value
        ? this.options
        : this.options.filter(option =>
            Object.values(option)
              .filter(optionValue => typeof optionValue === 'string' || typeof optionValue === 'number')
              .map(option => option.toString().toLowerCase())
              .find(optionVal => optionVal.includes(value.toLowerCase()))
          );
    }
  }

  private isSelectionValid(): boolean {
    return this.selectedItems && this.selectedItems.length > 0;
  }

  protected readonly toNumber = toNumber;
}
